/**
 * 1.7.0 stream B (docs/dev/formats-1.7.0-build.md): the format bar above the program monitor: the primary, each
 * format (its label, in its outline colour), then Guides (the primary with every format's crop outline) and Side by side
 * (every format at once). Choosing a format makes it the active one (the monitor, the inspector and the Reframe lane
 * follow it); in Guides the active format's outline is the one the graphics are shown in. Not shown for a sequence
 * without other formats (the overlays menu stays in the button row under the picture).
 */
import { Columns3, Frame } from 'lucide-react';
import type { FormatRef, ResolvedFormat } from '@prism/contracts';
import { formatColor, type FormatViewMode } from './formatContext';

export interface FormatBarProps {
  /** The document's formats, the primary first (formatsOf). */
  formats: ResolvedFormat[];
  active: FormatRef;
  mode: FormatViewMode;
  onActive: (id: FormatRef) => void;
  onMode: (mode: FormatViewMode) => void;
}


export function FormatBar({ formats, active, mode, onActive, onMode }: FormatBarProps) {
  if (formats.length < 2) return null;
  return (
    <div className="format-bar" role="toolbar" aria-label="Formats" data-testid="format-bar" data-active={active} data-mode={mode}>
      <div className="format-bar-formats" role="group" aria-label="Format shown">
        {formats.map((f) => (
          <button key={f.id} type="button" className={`format-chip ${f.id === active ? 'on' : ''}`} aria-pressed={f.id === active}
            title={`${f.label} · ${f.width}×${f.height}${f.isPrimary ? ' (primary)' : ''}`} onClick={() => onActive(f.id)}
            data-testid={`format-tab-${f.id}`} style={{ ['--chip' as string]: formatColor(f.index) }}>
            {!f.isPrimary && <span className="format-swatch" aria-hidden />}
            {f.label}
            {f.isPrimary && <span className="faint format-primary-mark">primary</span>}
          </button>
        ))}
      </div>
      <span className="spacer" />
      <button type="button" className={`btn ghost sm ${mode === 'guides' ? 'on' : ''}`} aria-pressed={mode === 'guides'} onClick={() => onMode(mode === 'guides' ? 'single' : 'guides')}
        title="The primary picture with each format's crop as an outline" data-testid="format-mode-guides"><Frame size={14} /> Guides</button>
      <button type="button" className={`btn ghost sm ${mode === 'side' ? 'on' : ''}`} aria-pressed={mode === 'side'} onClick={() => onMode(mode === 'side' ? 'single' : 'side')}
        title="Every format next to each other, playing together" data-testid="format-mode-side"><Columns3 size={14} /> Side by side</button>
    </div>
  );
}
